import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { PublicNav, PublicFooter } from "./PublicHome";
import { ArrowRight, Check, Info, X, Plus, MapPin } from "lucide-react";

const PACKAGE_LABELS = {
  "AD-SPOTLIGHT": { tagline: "Small local sponsored card targeted to your towns.",
    bullets: ["Up to 3 towns/cities in one state", "Professional Ad Creative + 1 revision included"] },
  "AD-FEATURED": { tagline: "Medium/wide local placement across more of your towns.",
    bullets: ["Up to 5 towns/cities in one state", "Professional Ad Creative + 1 revision included"] },
  "AD-PREMIUM": { tagline: "Large premium banner across your local towns.",
    bullets: ["Up to 10 towns/cities in one state", "Professional Ad Creative + 1 revision included"] },
  "AD-COUNTY": { tagline: "Reach customers throughout your entire county.",
    bullets: ["One entire county", "Premium 1200 × 300 banner", "Professional Ad Creative + 1 revision", "County-level geographic targeting", "Advertising dashboard"] },
  "AD-MARKET": { tagline: "Premium sponsorship across one entire state.",
    bullets: ["One entire state", "Professional Ad Creative + 1 revision included"] },
  "AD-REGIONAL": { tagline: "Premium sponsorship across up to five states.",
    bullets: ["Up to 5 selected states", "Professional Ad Creative + 1 revision included"] },
  "AD-NATIONAL": { tagline: "Premium sponsorship across all eligible U.S. markets.",
    bullets: ["All eligible U.S. markets", "Professional Ad Creative + 1 revision included"] },
};
const SALES_EMAIL = "sales@connectmyhub.com";

const SIZE_DEMOS = [
  { label: "SPOTLIGHT AD", dims: "400 × 300", w: 400, h: 300, code: "AD-SPOTLIGHT" },
  { label: "FEATURED AD", dims: "728 × 250", w: 728, h: 250, code: "AD-FEATURED" },
  { label: "PREMIUM / COUNTY / MARKET / REGIONAL / NATIONAL", dims: "1200 × 300", w: 1200, h: 300, code: "AD-PREMIUM" },
];

export default function Advertise() {
  const [state, setState] = useState({ packages: [], active_states: [], states: [] });
  const [selected, setSelected] = useState(null);
  const [statesSel, setStatesSel] = useState([]);   // Market / Regional
  const [geoState, setGeoState] = useState("");       // Towns / County single state
  const [townsSel, setTownsSel] = useState([]);       // Towns packages
  const [townInput, setTownInput] = useState("");
  const [countyInput, setCountyInput] = useState(""); // County package
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    website: "", business_description: "", destination_url: "",
    notes: "", approval_acknowledged: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { api.get("/public/sponsor/packages").then(r => setState(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!state.packages?.length) return;
    const pkg = new URLSearchParams(location.search).get("package");
    if (pkg && state.packages.find(p => p.code === pkg)) setSelected(pkg);
  }, [state.packages, location.search]);

  const current = useMemo(() => state.packages.find(p => p.code === selected) || null, [state.packages, selected]);
  const mode = current?.geography_mode;
  const maxStates = current?.max_states || 0;
  const maxTowns = current?.max_towns || 0;

  const toggleState = (code) => { setError(""); setStatesSel(prev =>
    prev.includes(code) ? prev.filter(s => s !== code) : prev.length >= maxStates ? prev : [...prev, code]); };

  const addTown = () => {
    const name = townInput.trim();
    if (!name) return;
    if (townsSel.some(t => t.toLowerCase() === name.toLowerCase())) { setTownInput(""); return; }
    if (townsSel.length >= maxTowns) return;
    setError(""); setTownsSel([...townsSel, name]); setTownInput("");
  };
  const removeTown = (name) => setTownsSel(townsSel.filter(t => t !== name));

  const startOrder = (code) => {
    setSelected(code); setStatesSel([]); setGeoState(""); setTownsSel([]); setTownInput(""); setCountyInput("");
    setResult(null); setError("");
    document.getElementById("intake")?.scrollIntoView({ behavior: "smooth" });
    navigate(`/advertise?package=${code}`, { replace: true });
  };

  const stateName = (code) => state.states.find(s => s.code === code)?.name || code;

  const coverageSummary = () => {
    if (!current) return "";
    if (mode === "SELECTED_TOWNS") return geoState ? (townsSel.length ? `${townsSel.join(", ")} (${stateName(geoState)})` : `— (${stateName(geoState)})`) : "—";
    if (mode === "SELECTED_COUNTY") return geoState && countyInput ? `${countyInput.trim()} County, ${stateName(geoState)}` : "—";
    if (mode === "SELECTED_STATES") return statesSel.length ? statesSel.map(stateName).join(", ") : "—";
    if (mode === "ALL_ACTIVE_US") return "All eligible U.S. ConnectMyHub markets";
    return "";
  };

  const validateGeo = () => {
    if (mode === "SELECTED_TOWNS") {
      if (!geoState) return "Please choose your state.";
      if (townsSel.length < 1) return "Please add at least one town/city.";
    }
    if (mode === "SELECTED_COUNTY") {
      if (!geoState) return "Please choose your state.";
      if (!countyInput.trim()) return "Please enter the county you want to target.";
    }
    if (mode === "SELECTED_STATES" && statesSel.length < 1) return "Please select at least one state.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!current) { setError("Please choose a package."); return; }
    const geoErr = validateGeo();
    if (geoErr) { setError(geoErr); return; }
    if (!form.approval_acknowledged) { setError("Please acknowledge the review policy."); return; }
    const payload = { ...form, package_code: current.code };
    if (mode === "SELECTED_TOWNS") { payload.selected_states = [geoState]; payload.selected_towns = townsSel; }
    else if (mode === "SELECTED_COUNTY") { payload.selected_states = [geoState]; payload.selected_county = countyInput.trim(); }
    else if (mode === "SELECTED_STATES") { payload.selected_states = statesSel; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/sponsor/orders", payload);
      let checkout = null;
      try {
        const cu = await api.post(`/public/sponsor/orders/${data.order_id}/checkout-url`);
        checkout = cu.data?.checkout_url || null;
      } catch (err) {
        const d = err?.response?.data?.detail;
        checkout = { _unavailable: true, message: d?.message || "Checkout is being configured. Our team will reach out." };
      }
      setResult({ ...data, checkout });
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail) || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const sel = "mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none";

  return (
    <div className="min-h-screen bg-white">
      <PublicNav/>
      <main>
        <section className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-300 mb-3">Sponsored Advertising</div>
            <h1 className="font-display font-bold text-3xl md:text-5xl leading-tight tracking-tight max-w-4xl" data-testid="advertise-headline">
              REACH THE BUSINESSES & PROFESSIONALS USING CONNECTMYHUB™
            </h1>
            <p className="text-slate-300 text-base md:text-lg mt-5 max-w-2xl">
              Target your advertising by town, county, state or nationwide across the ConnectMyHub marketplace — and only pay to reach the markets you choose.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/40 text-teal-200 rounded-full px-4 py-2 text-xs font-semibold" data-testid="pro-creative-badge">
              <Check size={14}/> PROFESSIONAL AD CREATIVE INCLUDED
            </div>
            <p className="text-slate-400 text-sm mt-3 max-w-2xl">Give us your website and business information. Our team creates your professional ConnectMyHub advertising creative for you. <span className="text-slate-300">One standard professional ad creative and one reasonable revision are included with each advertising purchase.</span></p>
            <p className="text-slate-400 text-sm mt-2">Questions or custom campaigns: <a href={`mailto:${SALES_EMAIL}`} className="text-teal-300 underline" data-testid="advertise-sales-email">{SALES_EMAIL}</a></p>
          </div>
        </section>

        {/* SIZE DEMOS */}
        <section className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Creative sizes</h2>
          <p className="text-sm text-slate-600 mb-8 max-w-2xl">See the relative shape of each ConnectMyHub ad format before you choose.</p>
          <div className="space-y-6">
            {SIZE_DEMOS.map(d => (
              <div key={d.dims} className="flex flex-col md:flex-row md:items-center gap-6" data-testid={`size-demo-${d.dims.replace(/\s|×/g,"")}`}>
                <div className="w-56 shrink-0">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{d.label}</div>
                  <div className="text-slate-900 font-semibold">{d.dims}</div>
                </div>
                <div className="flex-1 max-w-full">
                  <div className="relative border-2 border-dashed border-slate-300 bg-slate-50 rounded-md flex items-center justify-center text-slate-500 text-xs"
                    style={{ aspectRatio: `${d.w} / ${d.h}`, maxWidth: `min(100%, ${Math.min(d.w, 720)}px)` }}
                    data-testid={`size-box-${d.code}`}>
                    <span className="font-mono">{d.dims}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PACKAGE CARDS */}
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">Choose your advertising package</h2>
            <p className="text-sm text-slate-600 mb-8">Every package includes one standard professional ConnectMyHub advertising creative and one reasonable revision, built by our team. Market &amp; Regional sponsors may add <strong>+${state.additional_state_monthly || 50}/month per additional state</strong> where permitted.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {state.packages.map(p => (
                <div key={p.code} className={`rounded-xl bg-white border shadow-sm flex flex-col overflow-hidden ${p.code === "AD-COUNTY" ? "border-teal-400 ring-1 ring-teal-200" : "border-slate-200"}`} data-testid={`pkg-card-${p.code}`}>
                  <div className="p-5 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-600">{p.name.replace("ConnectMyHub ", "")}</div>
                      {p.code === "AD-COUNTY" && <span className="text-[9px] font-bold uppercase tracking-widest bg-teal-600 text-white px-1.5 py-0.5 rounded">New</span>}
                    </div>
                    <div className="mt-1 font-display font-bold text-lg text-slate-900">Creative size: {p.dimensions}</div>
                    <p className="text-sm text-slate-600 mt-2 min-h-[3rem]">{PACKAGE_LABELS[p.code]?.tagline || p.description}</p>
                    <div className="text-xs font-semibold text-slate-800 mt-1 inline-flex items-center gap-1" data-testid={`pkg-coverage-${p.code}`}><MapPin size={12} className="text-teal-600"/> {p.coverage}</div>
                    <div className="mt-4 flex items-baseline gap-1" data-testid={`pkg-price-${p.code}`}>
                      <span className="text-3xl font-display font-bold text-slate-900">${p.monthly_price.toLocaleString()}</span>
                      <span className="text-sm text-slate-500">/month</span>
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                      {(PACKAGE_LABELS[p.code]?.bullets || []).map(b => (
                        <li key={b} className="flex items-start gap-2"><Check size={14} className="text-teal-600 mt-0.5"/> {b}</li>
                      ))}
                    </ul>
                  </div>
                  <button onClick={() => startOrder(p.code)} data-testid={`pkg-choose-${p.code}`}
                          data-cta-mode={p.checkout_configured ? "purchase" : "sales"}
                          className="h-11 border-t border-slate-200 bg-slate-900 text-white text-xs font-semibold uppercase tracking-widest hover:bg-slate-800 flex items-center justify-center gap-2">
                    {p.checkout_configured ? "PURCHASE AD" : "GET STARTED"} <ArrowRight size={12}/>
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="additional-state-row">
              <div><span className="font-semibold text-slate-900">Additional State</span> — +${state.additional_state_monthly || 50}/month per additional state, available only on Market &amp; Regional Sponsor packages.</div>
              <a href={`mailto:${SALES_EMAIL}`} className="text-xs font-semibold uppercase tracking-widest text-teal-700 inline-flex items-center gap-1" data-testid="contact-sales-link">Contact Sales <ArrowRight size={12}/></a>
            </div>
          </div>
        </section>

        {/* INTAKE */}
        <section id="intake" className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">{current ? `Reserve — ${current.name}` : "Reserve your advertising"}</h2>
          <p className="text-sm text-slate-600 mb-6">
            {current ? (<>You selected <strong>{current.name}</strong> — <strong>${current.monthly_price}/month</strong> ({current.dimensions}). Choose your coverage and enter your business info below. All advertising is subject to ConnectMyHub / MPS review.</>)
              : "Choose a package above, then complete the short intake below."}
          </p>

          {result ? (
            <div className="rounded-xl border border-teal-300 bg-teal-50 p-6" data-testid="order-success">
              <div className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">Order received</div>
              <div className="text-slate-900 font-semibold">Order #{result.order_id}</div>
              <div className="text-sm text-slate-700 mt-1">Status: <span className="font-mono">{result.status}</span> · Monthly: ${result.monthly_total}</div>
              <div className="text-sm text-slate-700 mt-1" data-testid="order-coverage">Coverage: <strong>{coverageSummary()}</strong></div>
              {result.checkout?._unavailable ? (
                <div className="mt-4 rounded-md bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900" data-testid="checkout-unavailable">
                  <div className="flex items-start gap-2"><Info size={16} className="mt-0.5"/><div>{result.checkout.message} Contact sales: <a href={`mailto:${SALES_EMAIL}`} className="underline font-semibold">{SALES_EMAIL}</a></div></div>
                </div>
              ) : result.checkout ? (
                <a href={result.checkout} target="_blank" rel="noopener noreferrer" data-testid="checkout-link"
                   className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 text-white text-xs font-semibold uppercase tracking-widest hover:bg-slate-800">
                  Continue to secure checkout <ArrowRight size={12}/>
                </a>
              ) : null}
              <p className="text-xs text-slate-600 mt-3">After payment, our team will create your professional advertising creative and submit it for admin review before it goes live.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="sponsor-intake-form">
              {/* TOWNS packages */}
              {mode === "SELECTED_TOWNS" && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="towns-geo">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Target state</label>
                    <select value={geoState} onChange={e => { setError(""); setGeoState(e.target.value); setTownsSel([]); }} data-testid="towns-state-select" className={sel}>
                      <option value="">Choose a state…</option>
                      {state.states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Towns / cities <span className="text-slate-400">({townsSel.length}/{maxTowns})</span></label>
                    <div className="mt-1 flex gap-2">
                      <input value={townInput} onChange={e => setTownInput(e.target.value)}
                             onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTown(); } }}
                             disabled={!geoState || townsSel.length >= maxTowns}
                             placeholder={geoState ? "Type a town/city and add" : "Choose a state first"}
                             data-testid="town-input"
                             className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-100 outline-none focus:border-slate-500"/>
                      <button type="button" onClick={addTown} disabled={!geoState || !townInput.trim() || townsSel.length >= maxTowns}
                              data-testid="town-add-btn"
                              className="h-10 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40"><Plus size={14}/> Add</button>
                    </div>
                    {townsSel.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="town-chips">
                        {townsSel.map(t => (
                          <span key={t} data-testid={`town-chip-${t}`} className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full pl-3 pr-1.5 py-1 text-xs font-medium text-slate-800">
                            {t}<button type="button" onClick={() => removeTown(t)} data-testid={`town-remove-${t}`} className="rounded-full hover:bg-slate-200 p-0.5"><X size={12}/></button>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-2">This package covers up to {maxTowns} towns/cities within one state. Your ad will appear only in the markets you select.</p>
                  </div>
                </div>
              )}

              {/* COUNTY package */}
              {mode === "SELECTED_COUNTY" && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="county-geo">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Target state</label>
                    <select value={geoState} onChange={e => { setError(""); setGeoState(e.target.value); }} data-testid="county-state-select" className={sel}>
                      <option value="">Choose a state…</option>
                      {state.states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">County</label>
                    <input value={countyInput} onChange={e => { setError(""); setCountyInput(e.target.value); }} disabled={!geoState}
                           placeholder={geoState ? "e.g. Middlesex" : "Choose a state first"} data-testid="county-input"
                           className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-100 outline-none focus:border-slate-500"/>
                    <p className="text-[11px] text-slate-500 mt-2">Covers all eligible ConnectMyHub markets within one selected county. Surrounding counties and the rest of the state are not included.</p>
                  </div>
                </div>
              )}

              {/* STATES packages (Market / Regional) */}
              {mode === "SELECTED_STATES" && (
                <div data-testid="states-geo">
                  <label className="text-xs font-semibold text-slate-700">Select up to {maxStates} state{maxStates > 1 ? "s" : ""} <span className="text-slate-400">({statesSel.length}/{maxStates})</span></label>
                  <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5" data-testid="state-selector">
                    {state.active_states.map(s => {
                      const on = statesSel.includes(s);
                      const disabled = !on && statesSel.length >= maxStates;
                      return (
                        <button type="button" key={s} onClick={() => toggleState(s)} disabled={disabled} data-testid={`state-${s}`}
                                className={`h-9 rounded-md text-xs font-semibold border transition ${on ? "bg-slate-900 text-white border-slate-900" : disabled ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>{s}</button>
                      );
                    })}
                  </div>
                </div>
              )}

              {mode === "ALL_ACTIVE_US" && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="national-notice">
                  National coverage — your ad will be eligible across all eligible U.S. ConnectMyHub markets. No town, county or state selection is required.
                </div>
              )}

              {current && (
                <div className="rounded-md bg-white border border-slate-200 p-3 text-sm" data-testid="coverage-summary">
                  <span className="text-slate-500">Selected coverage:</span> <strong className="text-slate-900">{coverageSummary()}</strong>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Business name" v={form.business_name} on={v => setForm({...form, business_name: v})} testid="fld-business-name"/>
                <Field label="Website" v={form.website} on={v => setForm({...form, website: v})} testid="fld-website" type="url" placeholder="https://…"/>
                <Field label="Contact name" v={form.contact_name} on={v => setForm({...form, contact_name: v})} testid="fld-contact-name"/>
                <Field label="Contact email" v={form.email} on={v => setForm({...form, email: v})} testid="fld-email" type="email"/>
                <Field label="Contact phone" v={form.phone} on={v => setForm({...form, phone: v})} testid="fld-phone" type="tel"/>
                <Field label="Ad destination URL" v={form.destination_url} on={v => setForm({...form, destination_url: v})} testid="fld-destination" placeholder="https://…"/>
              </div>
              <Field label="Business / service description" v={form.business_description} on={v => setForm({...form, business_description: v})} testid="fld-description" textarea rows={3}
                     hint="What should your ad promote? Our creative team uses this to build your ConnectMyHub ad."/>
              <Field label="Notes (optional)" v={form.notes} on={v => setForm({...form, notes: v})} testid="fld-notes" textarea rows={2} required={false}/>

              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.approval_acknowledged} data-testid="fld-acknowledge"
                       onChange={e => setForm({...form, approval_acknowledged: e.target.checked})} className="mt-1"/>
                <span>I understand all advertising is subject to ConnectMyHub / My Pipeline Solutions review and may be rejected for any reason.</span>
              </label>

              {error && <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-sm p-3" data-testid="intake-error">{error}</div>}

              <button type="submit" disabled={submitting || !current} data-testid="submit-intake"
                      className="h-12 px-6 rounded-md bg-slate-900 text-white text-sm font-semibold disabled:opacity-50 hover:bg-slate-800 inline-flex items-center gap-2">
                {submitting ? "Submitting…" : current?.checkout_configured ? "Submit & purchase ad" : "Submit & contact sales"} <ArrowRight size={14}/>
              </button>
              <p className="text-xs text-slate-500">By submitting you agree to ConnectMyHub advertising policies. Payment does not automatically publish the advertisement.</p>
            </form>
          )}
        </section>

        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-4xl mx-auto px-6 py-12 text-sm text-slate-700 space-y-2">
            <div className="font-semibold text-slate-900">How ConnectMyHub advertising works</div>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Choose your advertising package.</li>
              <li>Select your coverage — towns, county, state or nationwide.</li>
              <li>Submit your business information.</li>
              <li>Complete payment.</li>
              <li>MPS creates the advertising creative for you.</li>
              <li>Advertising is reviewed before publication.</li>
              <li>Approved campaign enters ConnectMyHub rotation — shown only within your purchased coverage.</li>
            </ol>
            <div className="text-xs text-slate-500 pt-3">ConnectMyHub does not guarantee any specific number of impressions, clicks, leads, sales, or conversions.</div>
          </div>
        </section>
      </main>
      <PublicFooter/>
    </div>
  );
}

function Field({ label, v, on, testid, type = "text", placeholder, textarea = false, rows = 3, hint, required = true }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      {textarea ? (
        <textarea value={v} onChange={e => on(e.target.value)} rows={rows} required={required} data-testid={testid} placeholder={placeholder}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"/>
      ) : (
        <input type={type} value={v} onChange={e => on(e.target.value)} required={required} data-testid={testid} placeholder={placeholder}
               className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"/>
      )}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
